import React, { useState, useEffect, useCallback, useRef } from 'react';
import ErpTemplate from '../../../components/ErpTemplate';
import { FaBackward, FaSync, FaExclamationTriangle, FaTimes, FaSearch } from 'react-icons/fa';
import moment from 'moment';
import { RiMailSendLine, RiErrorWarningLine, RiCheckboxCircleLine, RiTimeLine } from 'react-icons/ri';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { useParams, Link, useLocation } from 'react-router-dom';
import { CardsCartaFila, RoscaCartaFila, estimativaFila, PERFIS_ENVIO_MASSA } from '../../../components/CartaFilaStatus';
import { FinField } from '../../../components/FinField';
import ReactSelect from '../../../components/ReactSelect';
import TabelaLoading from '../../../components/TabelaLoading';
import Data from '../../../components/Data';
import InputMask from 'react-input-mask';
import { dataHora } from '../../../util/util';
import '../../../css/financeiro-parcela-pagar.css';

/**
 * Progresso da fila de cartas de um evento.
 *
 * As cartas deixaram de sair todas de uma vez: o provedor do domínio aceita 100
 * mensagens do mesmo remetente a cada 5 minutos, e a rajada fazia ele recusar os
 * endereços internos em definitivo. Agora o clique enfileira e um agendador externo
 * processa aos poucos.
 *
 * Esta tela existe por causa disso: sem ela o operador clica em enviar, não vê nada
 * acontecer, e clica de novo — que foi como 126 cartas saíram duplicadas em 13/08.
 *
 * Sem id na rota, é a mesma tela para todos os eventos da empresa, só para quem dispara em
 * massa: os mesmos cards, filtros e tabela, com a coluna Evento. A fila é uma só, e quem
 * dispara em massa precisa do quadro inteiro sem abrir evento por evento.
 */
const INTERVALO_POLLING_MS = 5000;

const TIPOS_CARTA = [
    { value: 'convite', label: 'Convite' },
    { value: 'informativa', label: 'Informativa' },
    { value: 'certificado', label: 'Certificado' },
];
const LIMITE_ERRO = 160;

// Visoes que podem chegar pela URL (`?visao=pendente`), vindas do clique num card do painel de
// participantes. Qualquer outro valor abre nas falhas, como antes.
const VISOES_DA_URL = ['falhou', 'todos', 'pendente', 'processando', 'enviado'];

/**
 * Encurta o texto do erro para a coluna.
 *
 * O valor vem de getMessage() cru: falha de SMTP carrega host e porta, falha de banco
 * carrega trecho de SQL e nome de tabela. Numa tela que o operador abre, isso é
 * detalhe de infraestrutura exposto sem motivo — o texto inteiro fica no title, para
 * quem precisar investigar.
 */
function resumirErro(erro) {
    if (!erro) return '—';
    const limpo = String(erro).replace(/\s+/g, ' ').trim();
    return limpo.length > LIMITE_ERRO ? limpo.slice(0, LIMITE_ERRO) + '…' : limpo;
}

export default function PainelCartaFila() {
    const { id: idEvento } = useParams();
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');

    // Sem id, a fila de todos os eventos. Muda o escopo da consulta, os textos que falam "deste
    // evento" e a tabela, que ganha a coluna Evento — o resto e igual.
    const todosEventos = !idEvento;
    const noEscopo = todosEventos ? 'nos eventos' : 'neste evento';

    const [carregando, setCarregando] = useState(true);
    const [processandoManual, setProcessandoManual] = useState(false);

    // Diferente de `carregando`, que so liga nas consultas que trocam o que a tabela mostra
    // (abrir, card, Falhas/Todos, filtro, Atualizar): este acompanha toda consulta, inclusive
    // as do polling, para o titulo mostrar que a tela esta atualizando.
    const [buscando, setBuscando] = useState(false);
    const [totais, setTotais] = useState({ pendente: 0, processando: 0, enviado: 0, falhou: 0 });
    const [emAndamento, setEmAndamento] = useState(false);
    const [escopo, setEscopo] = useState('vazio');

    // Falhas deste envio, separadas do acumulado do evento: `totais.falhou` inclui falha
    // antiga de proposito (para o card nao zerar com a tabela cheia), e somar isso ao
    // progresso fazia a barra marcar 75% antes de a primeira carta do lote sair.
    const [falhasNoProcesso, setFalhasNoProcesso] = useState(0);
    // Ritmo com que a fila drena, vindo do back. Comeca nulo de proposito: ate a primeira
    // resposta chegar nao ha previsao para mostrar, e um default local viraria um numero
    // inventado na primeira renderizacao.
    const [ritmo, setRitmo] = useState(null);
    // O que esta na frente desta fila, somando todos os eventos: a fila e uma so, entao
    // carta de outro evento ocupa vaga da sua. Agregado, sem identificar de quem e.
    const [naFrente, setNaFrente] = useState(0);
    const [eventosNaFrente, setEventosNaFrente] = useState(0);
    const [comecou, setComecou] = useState(false);
    // As proximas cartas deste evento a sair, com a posicao na fila global. Sem os filtros da
    // tela: e a fila de verdade, que o back devolve independente do recorte pedido.
    const [proximas, setProximas] = useState([]);
    const [usuarios, setUsuarios] = useState([]);

    // Filtros da tela. Sem nenhum, ve-se o envio que esta acontecendo agora; com algum, o
    // que a operadora procurou. Guardados num objeto so para o `buscar` ter uma dependencia
    // unica — com quatro estados separados o useCallback recriaria a cada tecla.
    const [filtro, setFiltro] = useState({ tipo: '', usuario: '', lote: '', dataInicio: '', horaInicio: '', dataFim: '', horaFim: '' });

    // Que conjunto a tabela mostra. '' = falhas, que e como a tela abre. 'todos' = tudo.
    // Um status especifico vem do clique num card. Fica fora de `filtro` de proposito: nao
    // depende do Buscar, aplica na hora — e o gesto e "quero ver isto agora", nao "montei
    // um recorte e vou consultar".
    const { search } = useLocation();
    const [visao, setVisao] = useState(() => {
        const pedida = new URLSearchParams(search).get('visao');
        return VISOES_DA_URL.includes(pedida) ? pedida : '';
    });
    const [lotes, setLotes] = useState([]);

    // O que a tela realmente consulta, separado do que esta digitado. So o botao Buscar
    // move um para o outro.
    //
    // Nao e preciosismo: `filtro` e dependencia do `buscar`, entao aplicar direto fazia
    // cada tecla de "16:30" virar uma consulta. Com o polling das duas telas somado, isso
    // estourou a cota do Laravel (HTTP 429), que na tela aparece como "Nao foi possivel ler
    // o progresso da fila".
    const [filtroAplicado, setFiltroAplicado] = useState(filtro);

    const filtroVazio = { tipo: '', usuario: '', lote: '', dataInicio: '', horaInicio: '', dataFim: '', horaFim: '' };

    // Hora pela metade nao pode ser engolida: `09` com a mascara em `09:__` cairia no padrao
    // (00:00 ou 23:59) e a busca voltaria mais ampla do que a operadora pediu, sem nada
    // indicando. Melhor barrar o Buscar e dizer o que falta.
    const incompleta = (valor, formato) => Boolean(valor) && !moment(valor, formato, true).isValid();

    // A data entra na mesma checagem da hora. `13/08/202` passava: o Buscar continuava
    // habilitado, o paraMysql devolvia vazio, o parametro nao era enviado e a busca voltava
    // desde o comeco do evento — mais ampla do que a pedida, sem nada indicando.
    // Hora sem data tambem e descartada: o paraMysql monta o corte a partir da data, entao
    // "08:00" sozinho sumia igual a uma data pela metade, com o Limpar aparecendo e sugerindo
    // que havia filtro ativo.
    const horaOrfa = (Boolean(filtro.horaInicio) && !filtro.dataInicio)
        || (Boolean(filtro.horaFim) && !filtro.dataFim);

    const filtroInvalido = incompleta(filtro.horaInicio, 'HH:mm')
        || incompleta(filtro.horaFim, 'HH:mm')
        || incompleta(filtro.dataInicio, 'DD/MM/YYYY')
        || incompleta(filtro.dataFim, 'DD/MM/YYYY')
        || horaOrfa;
    const temAlgoPreenchido = Object.values(filtro).some((v) => v) || Object.values(filtroAplicado).some((v) => v);
    const [entradas, setEntradas] = useState([]);

    // Guarda o timer para o polling nunca duplicar quando a resposta demora mais que o
    // intervalo — dois timers vivos multiplicariam as chamadas até a tela travar.
    const timer = useRef(null);

    // Cancela a consulta anterior antes de abrir outra, como nas demais telas de painel.
    // Sem isso uma resposta lenta chega depois de uma rápida e sobrescreve o estado novo
    // com o velho — a barra anda para trás, ou o laço para com a fila ainda andando.
    const controllerRef = useRef(null);
    // Consulta em voo e desde quando: e o que deixa o tick do polling esperar a resposta em vez
    // de cancela-la. Ver o comentario no inicio de `buscar`.
    const emVooRef = useRef(false);
    const emVooDesdeRef = useRef(0);

    // Erros seguidos desligam o laço. Sem isso, servidor fora do ar vira um toast a cada
    // cinco segundos até alguém fechar a aba.
    const errosSeguidos = useRef(0);

    // Recusa ja avisada. Sem isto o toast repetiria a cada ciclo do polling.
    // Estado para o render; ref para a leitura dentro do `buscar`, que e useCallback e
    // congelaria o valor do render em que foi criado — a guarda "avisa uma vez so" nao
    // funcionava, e so nao aparecia porque o laco ja parava por outro caminho.
    const [recusado, setRecusado] = useState(false);
    const recusadoRef = useRef(false);

    // Para o laço sem mentir sobre a fila. `emAndamento` responde "ha carta na fila?" e tambem
    // escolhe o subtitulo — zera-lo para parar o polling fazia a tela dizer "Nenhuma carta na
    // fila" com o card de 240 pendentes logo abaixo. E a licao que o hook ja tinha aprendido.
    const [leituraPausada, setLeituraPausada] = useState(false);

    const buscar = useCallback((opcoes) => {
        // O tick do polling NAO atropela uma consulta em voo. Com o axios 0.27, que le `signal`,
        // abortar a cada tick fazia um `progresso` mais lento que o intervalo nunca voltar: a
        // tela parava de atualizar sem aviso, porque cancelamento nao conta como erro. Quem
        // muda o recorte chama sem opcao e aborta — ai a consulta anterior e que esta velha.
        //
        // O teto de idade existe porque a requisicao nao tem timeout: sem ele, uma consulta
        // presa para sempre faria o polling pular para sempre.
        if (opcoes && opcoes.doPolling && emVooRef.current
            && Date.now() - emVooDesdeRef.current < INTERVALO_POLLING_MS * 3) {
            return;
        }

        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        emVooRef.current = true;
        emVooDesdeRef.current = Date.now();

        setBuscando(true);
        // Loading na tabela so quando o conteudo dela vai ser trocado. Sem isto, ao clicar num
        // card o cabecalho mudava na hora e as linhas da visao anterior ficavam ate a resposta
        // chegar. O polling nao liga: a tabela piscaria a cada 5 segundos.
        if (!(opcoes && opcoes.doPolling)) {
            setCarregando(true);
        }

        // O componente Data trabalha em DD/MM/AAAA e o back espera AAAA-MM-DD. A conversao
        // fica aqui, no unico ponto que fala com a API, e nao no onChange de cada campo.
        // A hora e opcional e so afina o corte: em branco, o dia inteiro entra. E ela que
        // separa dois disparos do mesmo tipo, pela mesma pessoa, no mesmo dia — sem coluna
        // de lote, o horario do clique e o unico dado que os distingue.
        const paraMysql = (dataBr, horaBr, horaPadrao) => {
            const m = moment(dataBr, 'DD/MM/YYYY', true);
            if (!m.isValid()) return '';

            const hora = moment(horaBr, 'HH:mm', true).isValid() ? horaBr : horaPadrao;

            return m.format('YYYY-MM-DD') + ' ' + hora + ':00';
        };

        // Sem `evento`, o back devolve a fila de todos os eventos da empresa.
        const parametros = new URLSearchParams(idEvento ? { evento: idEvento } : {});
        const valores = {
            tipo: filtroAplicado.tipo,
            usuario: filtroAplicado.usuario,
            lote: filtroAplicado.lote,
            status: visao,
            dataInicio: paraMysql(filtroAplicado.dataInicio, filtroAplicado.horaInicio, '00:00'),
            dataFim: paraMysql(filtroAplicado.dataFim, filtroAplicado.horaFim, '23:59'),
        };
        Object.keys(valores).forEach((chave) => {
            if (valores[chave]) parametros.append(chave, valores[chave]);
        });

        // Desliga os indicadores. Sem `finally` de proposito: ele roda tambem na consulta que
        // acabou de ser abortada, e zerava o spinner com a nova ainda em voo — a tabela dizia
        // "nenhuma carta" enquanto buscava. A cancelada sai no catch antes de chegar aqui.
        // E o padrao da dev desde o axios 0.27 (PainelSlaDashboard).
        const encerrarBusca = () => {
            emVooRef.current = false;
            setCarregando(false);
            setBuscando(false);
        };

        api({
            method: 'get',
            action: `/carta/fila/progresso?${parametros.toString()}`,
            token: token,
            signal: controllerRef.current.signal,
        })
            .then(function (response) {
                encerrarBusca();
                const dados = response.data.data || {};

                // Recusa do back vem 200 com `mensagem` e tudo zerado. Gravar isso mostraria
                // "Nenhum envio em andamento", esvaziaria a tabela e pararia o polling — com o
                // lote correndo e sem um aviso. E o token expirando com esta tela aberta, que e
                // o uso citado no comentario do proprio controller.
                if (dados.mensagem) {
                    // Terminal: a recusa nao se resolve sozinha. Sem desligar o laco, o toast
                    // vermelho voltava a cada 5 segundos ate alguem fechar a aba — que e o que o
                    // comentario do errosSeguidos, logo acima, existe para impedir.
                    if (!recusadoRef.current) {
                        toast.error(dados.mensagem);
                    }

                    recusadoRef.current = true;

                    // Estado, e nao so toast: sem isto a tela seguia dizendo "Nenhum envio em
                    // andamento" e "Nenhuma carta falhou", com confianca, sem ter lido nada.
                    setRecusado(true);
                    setLeituraPausada(true);

                    return;
                }

                errosSeguidos.current = 0;
                recusadoRef.current = false;
                setRecusado(false);
                setLeituraPausada(false);

                // O back devolve 200 com apenas `mensagem` quando recusa (token expirado,
                // evento ausente). Sem este default, setTotais(undefined) passa calado e o
                // render seguinte estoura ao ler totais.pendente.
                setTotais(dados.totais || { pendente: 0, processando: 0, enviado: 0, falhou: 0 });
                setEmAndamento(Boolean(dados.emAndamento));
                setFalhasNoProcesso(dados.falhasNoProcesso || 0);
                setEscopo(dados.escopo || 'vazio');
                setRitmo(dados.ritmo || null);
                setNaFrente(dados.naFrente || 0);
                setEventosNaFrente(dados.eventosNaFrente || 0);
                setComecou(Boolean(dados.comecou));
                setProximas(dados.proximas || []);
                setUsuarios(dados.usuarios || []);
                setLotes(dados.lotes || []);
                setEntradas(dados.entradas || []);
            })
            .catch(function (err) {
                // Cancelamento nao e falha: e a consulta que nos mesmos substituimos.
                // Contar aqui desligaria o laco depois de tres buscas seguidas.
                if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;

                encerrarBusca();

                // 429 e o oposto de "servidor fora do ar": ele esta respondendo, e dizendo
                // que pedimos demais. Insistir de 5 em 5 segundos so afunda mais, e o texto
                // generico de falha mandava o operador procurar problema onde nao ha.
                if (err.response && err.response.status === 429) {
                    setLeituraPausada(true);
                    toast.error('Muitas consultas em pouco tempo. A atualização automática foi pausada — use Atualizar daqui a um minuto.');
                    return;
                }

                errosSeguidos.current += 1;

                if (errosSeguidos.current === 1) {
                    toast.error('Não foi possível ler o progresso da fila.');
                }

                if (errosSeguidos.current >= 3) {
                    setLeituraPausada(true);
                    toast.error('Progresso não está respondendo — a atualização automática foi desligada.');
                }
            });
    }, [idEvento, token, filtroAplicado, visao]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    // Aborta o que estiver em voo ao sair da tela, senao o `.then` acorda um componente
    // desmontado e o React acusa setState em nada.
    useEffect(() => () => {
        if (controllerRef.current) controllerRef.current.abort();
    }, []);

    useEffect(() => {
        // Só consulta em laço enquanto há carta na fila. Terminado o lote, a tela para de
        // pedir — polling que continua para sempre é custo sem informação nova.
        if (!emAndamento || leituraPausada) {
            if (timer.current) {
                clearInterval(timer.current);
                timer.current = null;
            }
            return;
        }

        timer.current = setInterval(() => buscar({ doPolling: true }), INTERVALO_POLLING_MS);

        return () => {
            if (timer.current) {
                clearInterval(timer.current);
                timer.current = null;
            }
        };
    }, [emAndamento, leituraPausada, buscar]);

    /**
     * Drena um ciclo da fila a mao.
     *
     * Valvula para quando o agendador externo esta parado: sem isto a fila fica em
     * `pendente` para sempre e nao ha como destravar sem acesso ao servidor.
     *
     * Fica desabilitado enquanto a chamada corre — nao por causa da concorrencia, que o
     * banco ja resolve, mas porque cada clique manda mais um ciclo de cartas e clicar em
     * sequencia recria a rajada que a fila existe para evitar.
     */
    const processarManual = () => {
        if (processandoManual) return;

        setProcessandoManual(true);

        api({ method: 'post', action: '/carta/fila/processarManual', token: token })
            .then(function (response) {
                const d = response.data.data || {};

                if (d.mensagem) {
                    toast.error(d.mensagem);
                    return;
                }

                // O ciclo e da fila inteira: as cartas que saem sao as mais antigas de qualquer
                // evento, ate por_ciclo no total. Com mais de um evento no ciclo, o toast diz
                // quantos; com um so, a mencao seria ruido — por isso e condicional.
                const eventos = d.eventos || 0;
                const deQuantosEventos = eventos > 1 ? ` (${eventos} eventos)` : '';

                toast.success(`${d.enviadas || 0} enviadas · ${d.falhas || 0} falhas${deQuantosEventos}`);
                buscar();
            })
            .catch(function () {
                toast.error('Não foi possível processar a fila.');
            })
            .finally(function () {
                setProcessandoManual(false);
            });
    };

    // O back manda id e nome; o ReactSelect quer {value,label}. String no value porque o id
    // e bigint e chega como texto — comparar numero com string erraria a opcao selecionada.
    const opcoesUsuarios = usuarios.map((u) => ({ value: String(u.id), label: u.nome || String(u.id) }));

    // O `value` e o `created_at` cru, que e o que o back compara. O rotulo traz carta, hora,
    // quem disparou e o tamanho — sem isso a lista seria uma coluna de datas iguais.
    // Grupo de uma carta so nao e lote: e envio individual ou reenvio, e o rotulo diz isso
    // para o operador nao procurar um disparo em massa que nunca existiu.
    const opcoesLotes = lotes.map((l) => {
        const rotuloTipo = (TIPOS_CARTA.find((t) => t.value === l.tipo) || {}).label || l.tipo;
        const quem = l.usuario_nome ? ` · ${l.usuario_nome}` : '';
        // Na fila de todos os eventos o seletor junta disparos de eventos diferentes.
        const doEvento = todosEventos && l.evento_nome ? `${l.evento_nome} · ` : '';

        return {
            value: l.created_at,
            label: Number(l.total) === 1
                ? `${doEvento}Individual · ${rotuloTipo} · ${dataHora(l.created_at)}${quem}`
                : `${doEvento}${rotuloTipo} · ${dataHora(l.created_at)}${quem} · ${l.total} cartas`,
        };
    });

    // Rotulo da secao da tabela. O cabecalho descreve o que esta logo abaixo dele; se a
    // visao muda e o titulo continua "Falhas", a tela passa a mentir sobre a propria tabela.
    const FALHAS_VISAO = {
        titulo: 'Falhas',
        sub: 'Cartas que a fila tentou enviar e o servidor recusou',
        vazio: `Nenhuma carta falhou ${noEscopo}`,
    };
    const VISOES = {
        '':          FALHAS_VISAO,
        falhou:      FALHAS_VISAO,
        todos:       { titulo: 'Todos os envios',   sub: `Cada carta enfileirada ${noEscopo}, em qualquer situação`, vazio: `Nenhuma carta enfileirada ${noEscopo}` },
        pendente:    { titulo: 'Aguardando envio',  sub: 'Cartas na fila, ainda não processadas',                        vazio: 'Nenhuma carta aguardando envio' },
        processando: { titulo: 'Saindo agora',      sub: 'Cartas que o ciclo está enviando neste momento',                vazio: 'Nenhuma carta saindo neste momento' },
        enviado:     { titulo: 'Enviadas',          sub: 'Cartas que saíram com sucesso',                                vazio: 'Nenhuma carta enviada neste recorte' },
    };
    const visaoAtual = VISOES[visao] || FALHAS_VISAO;
    const soFalhas = visao === '' || visao === 'falhou';
    const limiteTabela = soFalhas ? 50 : 200;

    // Icone e cor do cabecalho da tabela por visao: os mesmos do card que leva a ela, para o
    // operador reconhecer pelo desenho qual recorte esta vendo. `sempre` segue os cards —
    // Enviadas e Total tem cor fixa; Falhas e Aguardando so colorem quando ha o que mostrar.
    const ICONE_VISAO = {
        falhou:      { Icone: RiErrorWarningLine,   fundo: 'var(--fin-dgb)', cor: 'var(--fin-dg)', sempre: false },
        pendente:    { Icone: RiMailSendLine,       fundo: 'var(--fin-wnb)', cor: 'var(--fin-wn)', sempre: false },
        processando: { Icone: RiMailSendLine,       fundo: 'var(--fin-acw)', cor: 'var(--fin-ac)', sempre: false },
        enviado:     { Icone: RiCheckboxCircleLine, fundo: 'var(--fin-okb)', cor: 'var(--fin-ok)', sempre: true },
        todos:       { Icone: RiTimeLine,           fundo: 'var(--fin-acw)', cor: 'var(--fin-ac)', sempre: true },
    };
    const iconeVisao = ICONE_VISAO[soFalhas ? 'falhou' : visao] || ICONE_VISAO.falhou;
    const IconeVisao = iconeVisao.Icone;

    // A coluna Situacao so existe fora da visao de falhas — na de falhas todas as linhas tem
    // o mesmo status, e uma coluna com o mesmo valor em toda linha e ruido.
    const colunasTabela = (soFalhas ? 4 : 5) + (todosEventos ? 1 : 0);

    // Mesmas cores da marca na listagem de participantes: quem ja aprendeu que amarelo e
    // fila e azul e saindo agora nao precisa reaprender aqui.
    const SITUACAO = {
        pendente:    { rotulo: 'Na fila',     classe: 'is-warn' },
        processando: { rotulo: 'Saindo',      classe: 'is-info' },
        enviado:     { rotulo: 'Enviada',     classe: 'is-ok' },
        falhou:      { rotulo: 'Falhou',      classe: 'is-danger' },
    };

    const naFila = totais.pendente + totais.processando;
    const concluidas = totais.enviado + (escopo === 'processo' ? falhasNoProcesso : totais.falhou);
    const total = naFila + concluidas;
    const percentual = total === 0 ? 0 : Math.round((concluidas / total) * 100);

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title" style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                            {todosEventos ? 'Fila de envio das cartas · todos os eventos' : 'Fila de envio das cartas'}
                            {/* `baseline` em vez de `center`: um circulo nao tem baseline propria, entao o flex
                                usa a borda de baixo dele — ele se apoia na linha do texto, como uma
                                letra. Evita numero magico, que mudaria junto com a fonte do titulo. */}
                            {buscando ? <span className="fin-pp__spinner fin-pp__spinner--sm" /> : null}
                        </h1>
                        <p className="fin-pp__subtitle">
                            {recusado
                                ? 'Não foi possível ler a fila'
                                : leituraPausada
                                ? 'Atualização automática pausada — use Atualizar'
                                : emAndamento
                                    ? 'As cartas saem aos poucos, para não sobrecarregar o servidor de e-mail · esta tela se atualiza sozinha'
                                    : (todosEventos ? 'Nenhuma carta na fila' : 'Nenhuma carta na fila para este evento')}
                        </p>
                    </div>
                    <div className="fin-pp__actions">
                        <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm" onClick={() => { recusadoRef.current = false; setRecusado(false); buscar(); }}>
                            <FaSync size={14} /> Atualizar
                        </button>
                        {/* Mesmo desenho do "Disparar alertas" do PainelSla: gatilho manual de um
                            job que normalmente e agendado, marcado com a pilula `admin`. Quem
                            restringe de verdade e o controller — a condicao aqui so esconde um
                            botao que nao funcionaria. */}
                        {perfil === 'admin' ? (
                            <button
                                type="button"
                                className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm"
                                disabled={processandoManual}
                                title="Envia agora o próximo ciclo de cartas, sem esperar o agendador — somente admin"
                                onClick={processarManual}>
                                <RiMailSendLine size={14} /> {processandoManual ? 'Processando...' : 'Processar fila'}
                                <span className="fin-pp__pill is-warn" style={{ marginLeft: 6 }}>admin</span>
                            </button>
                        ) : null}
                        {todosEventos ? (
                            <Link to="/evento" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                                <FaBackward size={14} /> Eventos
                            </Link>
                        ) : (
                            <>
                                {/* Da fila de um evento para a de todos, para quem pode abri-la. */}
                                {PERFIS_ENVIO_MASSA.includes(perfil) ? (
                                    <Link to="/cartaFila" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                                        <RiMailSendLine size={14} /> Todos os eventos
                                    </Link>
                                ) : null}
                                <Link to={`/eventoParticipante/${idEvento}`} className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                                    <FaBackward size={14} /> Participantes
                                </Link>
                            </>
                        )}
                    </div>
                </div>

                {/* Mesmos cards do painel de participantes: quem clica no card de la
                    chega aqui e reencontra os mesmos quatro numeros, no mesmo lugar.
                    Sem idEvento, o ultimo card nao vira link para esta propria tela. */}
                {recusado ? (
                    <div className="fin-pp__banner is-warn">
                        <FaExclamationTriangle size={16} className="fin-pp__banner-ic" />
                        <span>
                            Não foi possível ler a fila{todosEventos ? '' : ' deste evento'} — sua sessão pode
                            ter expirado. Entre novamente e recarregue a página.
                        </span>
                    </div>
                ) : escopo === 'vazio' ? (
                    <div className="fin-pp__banner is-info">
                        <RiMailSendLine size={16} className="fin-pp__banner-ic" />
                        <span>
                            Nenhum envio em andamento {noEscopo}. Use os filtros abaixo para consultar
                            envios anteriores.
                        </span>
                    </div>
                ) : (
                    <CardsCartaFila totais={totais} escopo={escopo} falhasNoProcesso={falhasNoProcesso} aoEscolherStatus={setVisao} visaoAtiva={visao} compacto={false} ritmo={ritmo} naFrente={naFrente} eventosNaFrente={eventosNaFrente} comecou={comecou} />
                )}
                {!recusado && (
                    <RoscaCartaFila
                        aguardando={naFila}
                        enviadas={totais.enviado}
                        falhas={escopo === 'processo' ? falhasNoProcesso : totais.falhou}
                        proximas={proximas}
                        ritmo={ritmo}
                        mostrarEvento={todosEventos} />
                )}

                {/* Filtros abaixo dos cards: os numeros sao a resposta, o filtro e o ajuste.
                    Sem nenhum preenchido a tela mostra o envio em andamento; ao preencher
                    qualquer um, mostra o que foi pedido — cards, barra e tabela de falhas
                    juntos, para a soma da tabela sempre bater com o card. */}
                <form
                    className="fin-pp__card"
                    style={{ padding: 16, marginBottom: 16 }}
                    onSubmit={(e) => {
                        e.preventDefault();
                        // Copia, e nao a mesma referencia: com o objeto identico o React sai pelo
                        // bail-out, o useCallback nao muda e o segundo Buscar nao dispara nada —
                        // clique sem reacao, que e o gesto que leva ao reclique.
                        setFiltroAplicado({ ...filtro });
                    }}>
                    <div className="fin-pp__filters">
                        <FinField label="Carta">
                            <ReactSelect
                                isClearable
                                placeholder="Todas"
                                options={TIPOS_CARTA}
                                value={filtro.tipo ? TIPOS_CARTA.find((o) => o.value === filtro.tipo) : null}
                                onChange={(e) => setFiltro({ ...filtro, tipo: e ? e.value : '' })} />
                        </FinField>
                        <FinField label="Lote">
                            <ReactSelect
                                isClearable
                                placeholder="Todos"
                                options={opcoesLotes}
                                value={filtro.lote ? opcoesLotes.find((o) => o.value === filtro.lote) : null}
                                onChange={(e) => setFiltro({ ...filtro, lote: e ? e.value : '' })} />
                        </FinField>
                        <FinField label="Quem enviou">
                            <ReactSelect
                                isClearable
                                placeholder="Todos"
                                options={opcoesUsuarios}
                                value={filtro.usuario ? opcoesUsuarios.find((o) => o.value === filtro.usuario) : null}
                                onChange={(e) => setFiltro({ ...filtro, usuario: e ? e.value : '' })} />
                        </FinField>
                        <Data
                            id="dataInicio"
                            label="De"
                            value={filtro.dataInicio}
                            onChange={(v) => setFiltro({ ...filtro, dataInicio: v })} />
                        <FinField label="Hora inicial">
                            <InputMask
                                mask="99:99"
                                className="fin-pp__input"
                                id="horaInicio"
                                placeholder="00:00"
                                style={{ paddingLeft: 14 }}
                                value={filtro.horaInicio}
                                onChange={(e) => setFiltro({ ...filtro, horaInicio: e.target.value })} />
                        </FinField>
                        <Data
                            id="dataFim"
                            label="Até"
                            value={filtro.dataFim}
                            onChange={(v) => setFiltro({ ...filtro, dataFim: v })} />
                        <FinField label="Hora final">
                            <InputMask
                                mask="99:99"
                                className="fin-pp__input"
                                id="horaFim"
                                placeholder="23:59"
                                style={{ paddingLeft: 14 }}
                                value={filtro.horaFim}
                                onChange={(e) => setFiltro({ ...filtro, horaFim: e.target.value })} />
                        </FinField>
                    </div>

                    <div className="fin-pp__actions" style={{ marginTop: 12 }}>
                        <button
                            type="submit"
                            className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--primary fin-pp__btn--sm"
                            disabled={filtroInvalido}
                            title={filtroInvalido ? (horaOrfa ? 'Informe a data do horário' : 'Complete a data (DD/MM/AAAA) e a hora (HH:MM)') : undefined}>
                            <FaSearch size={13} /> Buscar
                        </button>
                        {filtroInvalido ? (
                            <span className="fin-pp__minitag is-warn">{horaOrfa ? 'Informe a data do horário' : 'Complete a data e a hora'}</span>
                        ) : null}
                        {temAlgoPreenchido ? (
                            <button
                                type="button"
                                className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm"
                                onClick={() => { setFiltro(filtroVazio); setFiltroAplicado(filtroVazio); }}>
                                <FaTimes size={13} /> Limpar
                            </button>
                        ) : null}
                    </div>
                </form>

                {/* Nao renderiza no escopo vazio: ali os contadores sao so as falhas, e a barra
                    diria "6 de 6 processadas · 100%" em verde sobre seis cartas que falharam. */}
                {total > 0 && escopo !== 'vazio' && !recusado && (
                    <div className="fin-pp__card" style={{ padding: '14px 18px', marginBottom: 18 }}>
                        <div style={{ height: 8, background: 'var(--fin-sf2)', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ height: 8, width: `${percentual}%`, background: 'var(--fin-ok)', transition: 'width .4s' }} />
                        </div>
                        <p className="fin-pp__subtitle" style={{ margin: '8px 0 0' }}>
                            {concluidas} de {total} processadas · {percentual}%
                            {escopo === 'filtrado' ? ' · do recorte filtrado' : ' · deste envio'}
                        </p>
                    </div>
                )}

                <div className="fin-pp__cphead">
                    {/* Conta as linhas da tabela, nao `totais.falhou`: os totais sao do processo
                        atual e zeram quando a fila esvazia, enquanto a tabela continua listando as
                        falhas do evento — dava visto verde com quatro falhas logo abaixo.
                        O cabecalho descreve a tabela, entao ele conta a tabela.

                        Icone e cor seguem a visao escolhida (ICONE_VISAO). Na de falhas, sem falha
                        fica no tom base da caixa, porque nada aconteceu; com falha, vermelho. */}
                    {/* Cor inline, e nao um `.fin-pp__cphead-ic.is-danger` no design system: essa
                        regra nao existia, e cria-la pintaria de vermelho o cabecalho "Retencoes" do
                        CadastroNotaFiscal, que ja escreve a classe e nao e estado de erro. Uma tela
                        fora do escopo desta entrega nao muda de cor por causa dela. */}
                    <span
                        className="fin-pp__cphead-ic"
                        style={iconeVisao.sempre || entradas.length > 0 ? { background: iconeVisao.fundo, color: iconeVisao.cor } : null}>
                        <IconeVisao size={16} />
                    </span>
                    <div>
                        <h2 className="fin-pp__cphead-title">{visaoAtual.titulo}</h2>
                        <p className="fin-pp__cphead-sub">
                            {visaoAtual.sub}
                            {/* O back corta a lista. Sem dizer isso, um evento com 87 entradas mostra
                                87 no card e 50 aqui, e nada explica o que sumiu. */}
                            {entradas.length >= limiteTabela ? ' · mostrando as ' + limiteTabela + ' mais recentes' : ''}
                        </p>
                    </div>
                    {/* Alternador da visao. Fica no cabecalho da secao, e nao junto dos filtros,
                        porque nao e recorte de consulta: e a escolha do que a tabela lista. Os
                        filtros continuam valendo por cima da visao escolhida. */}
                    <div className="fin-pp__actions" style={{ marginLeft: 'auto' }}>
                        <button
                            type="button"
                            className={'fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm' + (soFalhas ? ' fin-pp__btn--primary' : '')}
                            onClick={() => setVisao('')}>
                            Falhas
                        </button>
                        <button
                            type="button"
                            className={'fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm' + (visao === 'todos' ? ' fin-pp__btn--primary' : '')}
                            onClick={() => setVisao('todos')}>
                            Todos os envios
                        </button>
                    </div>
                </div>

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ minWidth: 820, tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th>Participante</th>
                                    {todosEventos ? <th>Evento</th> : null}
                                    <th>Carta</th>
                                    {soFalhas ? null : <th>Situação</th>}
                                    <th>Quando</th>
                                    <th>{soFalhas ? 'Erro' : 'Observação'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {carregando ? (
                                    <tr><td colSpan={colunasTabela} className="fin-pp__empty" style={{ padding: 0 }}><TabelaLoading texto="Carregando..." /></td></tr>
                                ) : entradas.length === 0 ? (
                                    <tr><td colSpan={colunasTabela} className="fin-pp__empty">{recusado ? 'Não foi possível ler a fila' : visaoAtual.vazio}</td></tr>
                                ) : (
                                    entradas.map((f) => (
                                        <tr key={f.id}>
                                            <td className="fin-pp__cell-strong">{f.participante_nome || f.participante}</td>
                                            {/* O nome leva para a fila daquele evento, com os cards so dele. */}
                                            {todosEventos ? (
                                                <td>
                                                    <Link to={`/cartaFila/${f.evento}`}>{f.evento_nome || f.evento}</Link>
                                                </td>
                                            ) : null}
                                            <td>
                                                <span className="fin-pp__minitag">
                                                    <RiMailSendLine size={11} /> {f.tipo}
                                                </span>
                                            </td>
                                            {soFalhas ? null : (
                                                <td>
                                                    <span className={'fin-pp__pill' + ((SITUACAO[f.status] || {}).classe ? ' ' + SITUACAO[f.status].classe : '')}>
                                                        {(SITUACAO[f.status] || {}).rotulo || f.status}
                                                    </span>
                                                    {/* A previsao desta carta, e nao a do lote: posicao na fila global
                                                        dividida pela vazao. So em linha viva — carta enviada nao tem
                                                        posicao, e mostrar uma diria que ela ainda vai sair. */}
                                                    {f.posicao ? (
                                                        <div className="fin-pp__cell-muted">
                                                            {f.posicao}º · {estimativaFila(f.posicao, ritmo) ? `sai em ${estimativaFila(f.posicao, ritmo)}` : 'sem previsão'}
                                                        </div>
                                                    ) : null}
                                                </td>
                                            )}
                                            {/* Na visao de falhas o que importa e quando falhou, que e o
                                                `updated_at`. Nas demais, quando entrou na fila — e o
                                                `created_at` e o mesmo para o lote inteiro, entao ele
                                                tambem identifica o disparo a que a linha pertence. */}
                                            <td className="fin-pp__cell-muted">{dataHora(soFalhas ? f.updated_at : f.created_at)}</td>
                                            <td className="fin-pp__cell-muted" title={f.erro}>{resumirErro(f.erro)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </ErpTemplate>
    );
}
